import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock, Lock, Globe, User as UserIcon, Plus, Loader2, Unlock,
  ShieldCheck, Inbox, Send as SendIcon, KeyRound, AlertTriangle, Sparkles,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useDisplayName } from '../hooks/useNip05Verified';
import {
  usePublicCapsules, useMyCapsules, useReceivedCapsules,
} from '../hooks/useTimeCapsules';
import {
  openCapsule, isUnlockable, CAPSULE_TIERS,
  type TimeCapsule, type CapsuleVisibility,
} from '../lib/timeCapsule';
import CreateCapsuleModal from '../components/CreateCapsuleModal';
import { BCHLogo } from '../components/Layout';

// ── Live countdown ─────────────────────────────────────────────────
function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function formatCountdown(secsLeft: number): string {
  if (secsLeft <= 0) return 'Unlocked';
  const d = Math.floor(secsLeft / 86400);
  const h = Math.floor((secsLeft % 86400) / 3600);
  const m = Math.floor((secsLeft % 3600) / 60);
  const s = Math.floor(secsLeft % 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

const VIS_META: Record<CapsuleVisibility, { icon: typeof Globe; label: string; color: string }> = {
  public: { icon: Globe, label: 'Public', color: 'text-sky-500' },
  addressed: { icon: UserIcon, label: 'Addressed', color: 'text-purple-500' },
  private: { icon: Lock, label: 'Private', color: 'text-amber-500' },
};

// ── One capsule card ────────────────────────────────────────────────
function CapsuleCard({ capsule }: { capsule: TimeCapsule }) {
  const now = useNow();
  const { profile: authorProfile } = useNostrProfile(capsule.pubkey);
  const authorName = useDisplayName(capsule.pubkey, authorProfile, capsule.pubkey.slice(0, 8));
  const { profile: recipProfile } = useNostrProfile(capsule.recipient);
  const recipName = useDisplayName(capsule.recipient, recipProfile, capsule.recipient?.slice(0, 8) || '');

  const secsLeft = capsule.unlockAt - Math.floor(now / 1000);
  const unlocked = isUnlockable(capsule);

  const [revealing, setRevealing] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);

  const Vis = VIS_META[capsule.visibility];

  const handleReveal = async () => {
    setRevealing(true);
    setRevealError(null);
    try {
      const text = await openCapsule(capsule.ciphertext);
      setRevealed(text);
    } catch (e) {
      // Most common cause: the drand round for this capsule hasn't been
      // published yet (clock skew right at the unlock boundary).
      setRevealError(
        e instanceof Error && /round|beacon|future/i.test(e.message)
          ? 'Almost there — the time-lock beacon for this moment hasn’t been published yet. Try again shortly.'
          : (e instanceof Error ? e.message : 'Could not open this capsule.'),
      );
    } finally {
      setRevealing(false);
    }
  };

  return (
    <div className={`rounded-xl border p-4 transition-colors ${
      unlocked ? 'border-emerald-500/40 bg-emerald-500/[0.04]' : 'border-border bg-card'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-sm truncate">{capsule.title || 'Untitled capsule'}</h3>
          <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
            <Link to={`/profile/${capsule.pubkey}`} className="hover:underline flex items-center gap-1">
              {authorProfile?.picture
                ? <img src={authorProfile.picture} alt="" className="w-4 h-4 rounded-full object-cover" loading="lazy" />
                : <span className="w-4 h-4 rounded-full bg-muted flex items-center justify-center"><UserIcon className="w-2.5 h-2.5" /></span>}
              {authorName}
            </Link>
            <span className={`flex items-center gap-1 ${Vis.color}`}>
              <Vis.icon className="w-3 h-3" /> {Vis.label}
            </span>
          </div>
        </div>
        <div className={`shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium ${
          unlocked ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-muted text-muted-foreground'
        }`}>
          {unlocked ? <Unlock className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
          {formatCountdown(secsLeft)}
        </div>
      </div>

      {capsule.visibility === 'addressed' && capsule.recipient && (
        <p className="text-[11px] text-muted-foreground mt-2">
          For <Link to={`/profile/${capsule.recipient}`} className="text-foreground hover:underline">{recipName}</Link>
        </p>
      )}

      <div className="mt-3 grid grid-cols-2 gap-2 text-[10px] text-muted-foreground">
        <div>Sealed<br /><span className="text-foreground">{new Date(capsule.createdAt * 1000).toLocaleDateString()}</span></div>
        <div>Unlocks<br /><span className="text-foreground">{new Date(capsule.unlockAt * 1000).toLocaleString()}</span></div>
      </div>

      {/* Message area */}
      <div className="mt-3">
        {revealed != null ? (
          <div className="rounded-lg bg-background border border-emerald-500/30 p-3">
            <p className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1 mb-1.5">
              <Unlock className="w-3 h-3" /> Opened
            </p>
            <p className="text-sm whitespace-pre-wrap break-words">{revealed}</p>
          </div>
        ) : unlocked ? (
          <div className="space-y-2">
            <button
              onClick={handleReveal}
              disabled={revealing}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 disabled:opacity-60 transition-colors"
            >
              {revealing ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              {revealing ? 'Opening…' : 'Open capsule'}
            </button>
            {revealError && (
              <p className="text-[11px] text-red-500 flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {revealError}
              </p>
            )}
          </div>
        ) : (
          <div className="rounded-lg bg-muted/50 border border-dashed border-border p-4 text-center">
            <Lock className="w-5 h-5 text-muted-foreground/50 mx-auto mb-1.5" />
            <p className="text-[11px] text-muted-foreground">
              Sealed with time-lock encryption.<br />Unreadable until the countdown ends.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────
type Tab = 'public' | 'mine' | 'received';

export default function TimeCapsulesPage() {
  const { user, setShowLoginDialog } = useAppContext();
  const [tab, setTab] = useState<Tab>('public');
  const [showCreate, setShowCreate] = useState(false);

  const { capsules: publicCapsules, loading: loadingPublic, refetch: refetchPublic } = usePublicCapsules();
  const { capsules: myCapsules, loading: loadingMine, refetch: refetchMine } = useMyCapsules(user?.pubkey);
  const { capsules: receivedCapsules, loading: loadingReceived } = useReceivedCapsules(user?.pubkey);

  const active = useMemo(() => {
    if (tab === 'mine') return { list: myCapsules, loading: loadingMine };
    if (tab === 'received') return { list: receivedCapsules, loading: loadingReceived };
    return { list: publicCapsules, loading: loadingPublic };
  }, [tab, myCapsules, loadingMine, receivedCapsules, loadingReceived, publicCapsules, loadingPublic]);

  const tabs: { id: Tab; label: string; icon: typeof Globe; auth?: boolean }[] = [
    { id: 'public', label: 'Public', icon: Globe },
    { id: 'mine', label: 'My Capsules', icon: SendIcon, auth: true },
    { id: 'received', label: 'Received', icon: Inbox, auth: true },
  ];

  return (
    <div className="max-w-2xl mx-auto pb-24">
      {/* Hero */}
      <div className="px-4 pt-5 pb-4">
        <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-emerald-500/5 border border-emerald-500/20 p-5">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Clock className="w-4.5 h-4.5 text-white" />
            </div>
            <h1 className="text-lg font-bold">Time Capsules</h1>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Seal a message that no one — not even you or BCHNostr — can read until a future date you choose.
            Locked with <strong className="text-foreground">time-lock encryption</strong>, unsealed automatically when the clock runs out.
          </p>
          <div className="flex items-center gap-3 mt-3">
            <button
              onClick={() => (user ? setShowCreate(true) : setShowLoginDialog(true))}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
            >
              <Plus className="w-4 h-4" /> Create capsule
            </button>
            <span className="text-[11px] text-muted-foreground flex items-center gap-1">
              <BCHLogo size={14} /> ${CAPSULE_TIERS.standard.priceUsd.toFixed(2)} in BCH to seal
            </span>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="px-4 pb-4">
        <div className="rounded-xl border border-border bg-muted/30 p-3 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Your message is encrypted in your browser and only the sealed version is published. It becomes readable to
            everyone it was addressed to the moment its unlock time arrives — powered by the drand time-lock network,
            with no key held by any server.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="flex px-4">
          {tabs.map(t => {
            const disabled = t.auth && !user;
            return (
              <button
                key={t.id}
                onClick={() => (disabled ? setShowLoginDialog(true) : setTab(t.id))}
                className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                } ${disabled ? 'opacity-50' : ''}`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <div className="px-4 pt-4 space-y-3">
        {active.loading && active.list.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            <p className="text-sm">Loading capsules…</p>
          </div>
        ) : active.list.length === 0 ? (
          <div className="text-center py-16">
            <Sparkles className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm font-medium">
              {tab === 'mine' ? 'You haven’t sealed any capsules yet'
                : tab === 'received' ? 'No capsules addressed to you yet'
                : 'No public capsules yet'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {tab === 'received' ? 'When someone seals one for you, it’ll appear here.' : 'Be the first to seal a message for the future.'}
            </p>
            {tab !== 'received' && (
              <button
                onClick={() => (user ? setShowCreate(true) : setShowLoginDialog(true))}
                className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" /> Create one
              </button>
            )}
          </div>
        ) : (
          active.list.map(c => <CapsuleCard key={c.id} capsule={c} />)
        )}
      </div>

      {showCreate && (
        <CreateCapsuleModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { refetchPublic(); refetchMine(); }}
        />
      )}
    </div>
  );
}
